import { useEffect, useState } from 'react'
import { AlertCircle, Download, Key, Loader2, Table2 } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  adoptExternalTable, getLiveTableSample,
  type LiveColumnInfo, type LiveIndexInfo, type SyncTarget,
} from '@/api/designers'

interface LiveStructureViewProps {
  project: string
  target: SyncTarget
  table: string
  canEdit: boolean
  /** Called after a successful adopt so the caller can re-select the table. */
  onAdopted: (designName: string) => void
}

/**
 * The Design tab for a table that exists only in the database.
 *
 * It reads the same as the design editor — same columns, same order — but is
 * READ-ONLY, because there is no design file to edit. Showing the real
 * structure is the honest answer to "what is this table?"; loading the design
 * editor would show an empty column list for a table that plainly has
 * columns. Adopting creates the design, after which the normal editor takes
 * over.
 */
export default function LiveStructureView({
  project, target, table, canEdit, onAdopted,
}: LiveStructureViewProps) {
  const [columns, setColumns] = useState<LiveColumnInfo[] | null>(null)
  const [indexes, setIndexes] = useState<LiveIndexInfo[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [adopting, setAdopting] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    getLiveTableSample(project, table, target, { limit: 0 })
      .then((res) => {
        if (cancelled) return
        if (res.success) {
          setColumns(res.columns || [])
          setIndexes(res.indexes || [])
        } else setError(res.message || 'Could not read this table.')
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not read this table.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [project, table, target])

  const handleAdopt = async () => {
    setAdopting(true)
    try {
      const res = await adoptExternalTable(project, table, target)
      if (res.success) {
        toast.success(res.message || `${table} adopted into the project`)
        onAdopted(res.table || table.toUpperCase())
      } else {
        toast.error(res.message || 'Could not adopt this table')
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not adopt this table')
    } finally {
      setAdopting(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Header — mirrors the design editor's, minus the editable fields */}
      <div style={{
        padding: '1rem 1.25rem', borderBottom: '1px solid var(--color-border)',
        flexShrink: 0,
      }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
            <Table2 size={20} style={{ color: '#c08a2e', flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontSize: '1.1875rem', fontWeight: 600, lineHeight: 1.4,
                color: 'var(--color-text)',
              }}>
                {table}
              </div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                Structure read from the live database
              </div>
            </div>
          </div>
          {canEdit && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleAdopt}
              disabled={adopting || !columns?.length}
              title="Create a Database_Design entry for this table (externally managed — deploy will never alter it)"
            >
              {adopting
                ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                : <Download size={14} />}
              Adopt into project
            </button>
          )}
        </div>
      </div>

      {/* Why this is read-only */}
      <div style={{
        display: 'flex', gap: '0.5rem', alignItems: 'flex-start', flexShrink: 0,
        margin: '0.75rem 1rem 0', padding: '0.5rem 0.75rem',
        borderRadius: 'var(--radius-md)', background: 'rgba(245,158,11,0.08)',
        border: '1px solid rgba(245,158,11,0.3)',
        fontSize: '0.8125rem', color: 'var(--color-text-secondary)', lineHeight: 1.5,
      }}>
        <AlertCircle size={14} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 1 }} />
        <span>
          This table is in the database but not in the project design, so its
          structure can be viewed but not edited here. Adopt it to add a design
          entry — it will be marked externally managed, so deploying never
          alters the table.
        </span>
      </div>

      {/* Columns — same shape as the design editor's column table */}
      <div style={{ flex: 1, overflow: 'auto', padding: '0.75rem 1rem' }}>
        {loading ? (
          <div className="flex items-center justify-center" style={{ padding: '2rem' }}>
            <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
          </div>
        ) : error ? (
          <div style={{
            display: 'flex', gap: '0.625rem', padding: '0.75rem 1rem',
            borderRadius: 'var(--radius-md)', background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.3)',
          }}>
            <AlertCircle size={16} style={{ color: '#ef4444', flexShrink: 0, marginTop: 2 }} />
            <div style={{
              fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
              fontFamily: 'var(--font-mono, monospace)', whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              {error}
            </div>
          </div>
        ) : (
          <div className="table-container" style={{ fontSize: '0.875rem', overflowX: 'auto' }}>
            <table style={{ minWidth: 560, width: '100%' }}>
              <colgroup>
                <col style={{ width: 30 }} />
                <col style={{ minWidth: 160 }} />
                <col style={{ width: 190 }} />
                <col style={{ width: 50 }} />
                <col style={{ width: 36 }} />
                <col style={{ minWidth: 120 }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={{ padding: '0.375rem 0.125rem', textAlign: 'center', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>#</th>
                  <th style={{ padding: '0.375rem 0.375rem' }}>Column Name</th>
                  <th style={{ padding: '0.375rem 0.25rem' }}>Data Type</th>
                  <th style={{ padding: '0.375rem 0.125rem', textAlign: 'center' }}>Null</th>
                  <th style={{ padding: '0.375rem 0.125rem', textAlign: 'center' }}>PK</th>
                  <th style={{ padding: '0.375rem 0.25rem' }}>Default</th>
                </tr>
              </thead>
              <tbody>
                {(columns || []).map((c, i) => (
                  <tr key={c.column} style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '0.3rem 0.125rem', textAlign: 'center', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                      {i + 1}
                    </td>
                    <td style={{ padding: '0.3rem 0.375rem', color: 'var(--color-text)' }}>
                      {c.column}
                    </td>
                    <td style={{ padding: '0.3rem 0.25rem', fontFamily: 'var(--font-mono, monospace)', color: 'var(--color-text-secondary)' }}>
                      {c.type}
                    </td>
                    <td style={{ padding: '0.3rem 0.125rem', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                      {c.nullable ? '✓' : ''}
                    </td>
                    <td style={{ padding: '0.3rem 0.125rem', textAlign: 'center' }}>
                      {c.pk && <Key size={12} style={{ color: '#f59e0b' }} />}
                    </td>
                    <td style={{
                      padding: '0.3rem 0.25rem', color: 'var(--color-text-secondary)',
                      maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }} title={c.default || ''}>
                      {c.default || '—'}
                    </td>
                  </tr>
                ))}
                {!columns?.length && (
                  <tr>
                    <td colSpan={6} style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                      No columns found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Indexes — read from the database, same section the design view
                shows for a project table. Read-only for the same reason the
                columns are: there is no design to change. */}
            <div style={{ marginTop: '1.5rem' }}>
              <div style={{
                fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem',
                color: 'var(--color-text-secondary)',
              }}>
                Indexes ({indexes.length})
                <span style={{ fontWeight: 400 }}> — as they exist in the database</span>
              </div>
              {indexes.length === 0 ? (
                <div style={{
                  border: '1px dashed var(--color-border)',
                  borderRadius: 'var(--radius-md)', padding: '0.75rem',
                  fontSize: '0.875rem', color: 'var(--color-text-secondary)',
                  fontStyle: 'italic',
                }}>
                  This table has no indexes.
                </div>
              ) : (
                <div style={{
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)', overflow: 'hidden',
                }}>
                  {indexes.map((ix, i) => (
                    <div
                      key={ix.name}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                        padding: '0.4rem 0.625rem', fontSize: '0.875rem',
                        borderTop: i ? '1px solid var(--color-border)' : undefined,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span style={{ color: 'var(--color-text)', fontWeight: 500 }}>
                        {ix.name}
                      </span>
                      <span style={{
                        color: 'var(--color-text-secondary)',
                        fontFamily: 'var(--font-mono, monospace)', fontSize: '0.8125rem',
                        flex: '1 1 auto', minWidth: 0,
                      }}>
                        ({ix.columns.join(', ')})
                      </span>
                      {ix.primary && (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3,
                          padding: '1px 7px', borderRadius: 10, fontSize: 13,
                          fontWeight: 600, color: '#fff', background: '#a855f7',
                        }}>
                          <Key size={10} /> PRIMARY
                        </span>
                      )}
                      {ix.unique && !ix.primary && (
                        <span style={{
                          padding: '1px 7px', borderRadius: 10, fontSize: 13,
                          fontWeight: 600, color: 'var(--color-text-secondary)',
                          background: 'var(--color-bg-secondary)',
                          border: '1px solid var(--color-border)',
                        }}>
                          UNIQUE
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
